import React, { useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import { useSelector } from 'react-redux';
import { AccountingGroup, WithId } from 'common/types';
import ProductDialog from 'components/ProductDialog';
import { useTranslation } from 'services/localization';
import { api } from 'common/frontend/api';
import { newFirestoreId } from 'common/utils/dataMigrations';
import Spinner from 'common/components/Spinner';
import { FieldValues } from 'common/frontend/firebase/firestore';

const CustomAccountingGroups = () => {
	const { t } = useTranslation();
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<Button variant="text" color="primary" onClick={() => setOpen(true)}>
				{t('reports.accounting.editAccountingGroups', 'Edit accounting groups')}
			</Button>
			<EditSectionsDialog open={open} handleClose={() => setOpen(false)} />
		</>
	);
};

interface DialogProps {
	open: boolean;
	handleClose: () => void;
}

const EditSectionsDialog = (props: DialogProps) => {
	const { t } = useTranslation();
	const customAccountingGroups = useSelector(ShopSelectors.customAccountingGroups);
	const noGroups = !customAccountingGroups.length;
	const shopId = useSelector(ShopSelectors.activeShopId)!;

	const addNewGroup = () => {
		const newGroup: AccountingGroup = {
			name: '',
			id: newFirestoreId(),
		};
		const newCustomAccountingGroups: AccountingGroup[] = [...customAccountingGroups, newGroup];
		api().shops.doc(shopId).update({
			'plugins.customAccountingGroups': newCustomAccountingGroups,
		});
	};

	return (
		<Dialog maxWidth="md" open={props.open} onClose={props.handleClose}>
			<DialogTitle>
				{t('reports.accounting.editAccountingGroups', 'Edit accounting groups')}
			</DialogTitle>
			<DialogContent>
				<Typography variant="body2">
					{t(
						'reports.accounting.accountingGroupsInfo',
						'You can add your own item grouping that will be visible in its own section in the accounting report. Add a group below and link products to the group. You can have a product assigned to only a single group.',
					)}
				</Typography>
				<Box mt={4} />
				{noGroups ? (
					<Typography variant="body1">
						{t('reports.accounting.noGroups', 'No groups created yet')}
					</Typography>
				) : (
					<>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>{t('reports.accounting.groupName', 'Group name')}</TableCell>
									<TableCell>{t('products.products', 'Products')}</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{customAccountingGroups.map((group) => (
									<CustomGroupRow group={group} />
								))}
							</TableBody>
						</Table>
					</>
				)}
				<Box mt={2}>
					<Button onClick={addNewGroup} color="primary">
						+ {t('reports.accounting.createNewGroup', 'Create new group')}
					</Button>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={props.handleClose} color="primary">
					{t('common:actions.close')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

interface GroupRowProps {
	group: WithId<AccountingGroup>;
}

const CustomGroupRow = (props: GroupRowProps) => {
	const { group } = props;
	const { t } = useTranslation();
	const customAccountingGroups = useSelector(ShopSelectors.customAccountingGroups);
	const [groupDialogOpen, setGroupDialogOpen] = useState<boolean>(false);
	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const productIdsWithGroup = stockProducts
		.filter((p) => p.customAccountingGroups?.includes(group.id))
		.map((p) => p.id);
	const productIdsWithOtherGroup = stockProducts
		.filter(
			(p) => !!p.customAccountingGroups?.length && !p.customAccountingGroups?.includes(group.id),
		)
		.map((p) => p.id);
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const [editName, setEditName] = useState<boolean>(group.name === '' ? true : false);
	const [groupName, setGroupName] = useState<string>(group.name);
	const [savingName, setSavingName] = useState<boolean>(false);
	const [savingProducts, setSavingProducts] = useState<boolean>(false);
	const [deletingGroup, setDeletingGroup] = useState<boolean>(false);

	const handleGroupDialogSave = (selectedProductIds: string[]) => {
		saveGroupProducts(selectedProductIds);
		setGroupDialogOpen(false);
	};

	const handleEditName = () => {
		setEditName(true);
		setGroupName(group.name);
	};

	const saveGroupName = async () => {
		setSavingName(true);
		if (group.name !== groupName) {
			const newCustomAccountingGroups: AccountingGroup[] = [
				...customAccountingGroups.filter((g) => g.id !== group.id),
				{
					...group,
					name: groupName,
				},
			];
			await api().shops.doc(shopId).update({
				'plugins.customAccountingGroups': newCustomAccountingGroups,
			});
		}
		setSavingName(false);
		setEditName(false);
	};

	const saveGroupProducts = async (selectedProductIds: string[]) => {
		setSavingProducts(true);
		const removedIds = productIdsWithGroup.filter((id) => !selectedProductIds.includes(id));
		const addedIds = selectedProductIds.filter((id) => !productIdsWithGroup.includes(id));
		await Promise.all([
			removedIds.map((id) =>
				api()
					.products.doc(id)
					.update({
						customAccountingGroups: FieldValues.arrayRemove(group.id),
					}),
			),
			addedIds.map((id) =>
				api()
					.products.doc(id)
					.update({
						customAccountingGroups: FieldValues.arrayUnion(group.id),
					}),
			),
		]);
		setSavingProducts(false);
	};

	const removeGroup = async () => {
		if (
			!window.confirm(
				t('reports.accounting.removeGroup', 'Are you sure that you want to remove this group?'),
			)
		) {
			return;
		}
		setDeletingGroup(true);
		const newCustomAccountingGroups: AccountingGroup[] = [
			...customAccountingGroups.filter((g) => g.id !== group.id),
		];
		await Promise.all([
			productIdsWithGroup.map((productId) => {
				return api()
					.products.doc(productId)
					.update({
						customAccountingGroups: FieldValues.arrayRemove(group.id),
					});
			}),
		]);
		await api().shops.doc(shopId).update({
			'plugins.customAccountingGroups': newCustomAccountingGroups,
		});
		setDeletingGroup(false);
	};

	return (
		<TableRow>
			<TableCell>
				<TextField
					label={t('common:form.name')}
					value={editName ? groupName : group.name}
					onChange={(e) => setGroupName(e.target.value)}
					InputProps={{
						disableUnderline: !editName,
						readOnly: !editName,
						endAdornment: editName ? (
							<Button variant="text" color="primary" onClick={saveGroupName}>
								{savingName ? <Spinner size={20} /> : t('common:actions.save')}
							</Button>
						) : (
							<Button variant="text" color="primary" onClick={handleEditName}>
								{t('common:actions.edit')}
							</Button>
						),
					}}
				/>
			</TableCell>
			<TableCell>
				<Button variant="outlined" color="primary" onClick={() => setGroupDialogOpen(true)}>
					{savingProducts ? (
						<Spinner size={20} />
					) : productIdsWithGroup.length ? (
						`${productIdsWithGroup.length} ${t('common:states.selected', 'Selected').toLowerCase()}`
					) : (
						t('common:actions.select').toLowerCase()
					)}
				</Button>
				<ProductDialog
					open={groupDialogOpen}
					onCancel={() => setGroupDialogOpen(false)}
					onSave={handleGroupDialogSave}
					products={stockProducts}
					activeProducts={productIdsWithGroup}
					disabledProducts={productIdsWithOtherGroup}
				/>
			</TableCell>
			<TableCell>
				<Button variant="text" color="secondary" onClick={removeGroup}>
					{deletingGroup ? <Spinner size={20} /> : t('common:actions.remove')}
				</Button>
			</TableCell>
		</TableRow>
	);
};

export default CustomAccountingGroups;
